import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../../contexts/ThemeContext";
import axios from "axios";
import { X, Activity, Code, Calendar, User } from "lucide-react";

interface VersionBadgeProps {
  className?: string;
}

interface VersionInfo {
  component: string;
  version: string;
  build_number: number;
  deployed_at: string;
  deployed_by?: string;
  git_commit?: string;
}

interface VersionResponse {
  frontend: VersionInfo;
  backend: VersionInfo;
}

const VersionBadge: React.FC<VersionBadgeProps> = ({ className = "" }) => {
  const { isDarkMode } = useTheme();
  const [version, setVersion] = useState<string>("...");
  const [isOnline, setIsOnline] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const [versionData, setVersionData] = useState<VersionResponse | null>(null);

  useEffect(() => {
    fetchVersion();
    const interval = setInterval(fetchVersion, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  const fetchVersion = async () => {
    try {
      const apiUrl =
        process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";
      const response = await axios.get(`${apiUrl}/version/current`, {
        timeout: 5000,
      });

      if (response.status === 200 && response.data) {
        setVersionData(response.data);
        const frontendVer = response.data.frontend?.version || "1.0.0";
        setVersion(`v${frontendVer}`);
        setIsOnline(true);
      } else {
        setIsOnline(false);
      }
    } catch (error) {
      setIsOnline(false);
      setVersion("v1.0.0");
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer ${
          isDarkMode
            ? "bg-gray-700/50 hover:bg-gray-700"
            : "bg-gray-100 hover:bg-gray-200"
        } ${className}`}
        title={`${
          isOnline ? "System Online" : "System Offline"
        } - Click to view details`}
      >
        {/* Status indicator */}
        <div className="relative flex items-center justify-center">
          <div
            className={`w-1.5 h-1.5 rounded-full ${
              isOnline ? "bg-green-500" : "bg-gray-400"
            }`}
          />
          {isOnline && (
            <div className="absolute w-1.5 h-1.5 rounded-full bg-green-500 animate-ping opacity-75" />
          )}
        </div>

        {/* Version text */}
        <span
          className={`text-xs font-mono font-semibold ${
            isDarkMode ? "text-gray-300" : "text-gray-700"
          }`}
        >
          {version}
        </span>
      </button>

      {/* Modal de detalles */}
      {createPortal(
        <AnimatePresence>
          {showModal && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
              {/* Backdrop con blur */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={() => setShowModal(false)}
              />

              {/* Modal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ type: "spring", duration: 0.5 }}
                className={`relative w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden ${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header con gradiente phoenix */}
                <div
                  className={`px-6 py-5 ${
                    isDarkMode
                      ? "bg-gradient-to-r from-phoenix-700 to-phoenix-800"
                      : "bg-gradient-to-r from-phoenix-600 to-phoenix-700"
                  } text-white`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <motion.div
                        initial={{ rotate: 0 }}
                        animate={{ rotate: 360 }}
                        transition={{ duration: 0.5 }}
                        className="p-2 bg-white/20 rounded-lg"
                      >
                        <Activity className="w-6 h-6" />
                      </motion.div>
                      <div>
                        <h2 className="text-2xl font-bold">
                          Version Information
                        </h2>
                        <p className="text-phoenix-100 text-sm mt-1">
                          Details of the deployed system
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowModal(false)}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                  {/* Frontend Info */}
                  {versionData?.frontend && (
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className={`border-l-4 border-blue-500 p-4 rounded-r-lg ${
                        isDarkMode ? "bg-gray-700/50" : "bg-blue-50"
                      }`}
                    >
                      <h3
                        className={`text-lg font-bold mb-3 flex items-center gap-2 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        <Code className="w-5 h-5 text-blue-500" />
                        Frontend
                      </h3>
                      <div className="space-y-2.5 text-sm">
                        <div className="flex justify-between items-center">
                          <span
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }
                          >
                            Version:
                          </span>
                          <span
                            className={`font-mono font-bold text-base ${
                              isDarkMode ? "text-blue-400" : "text-blue-600"
                            }`}
                          >
                            v{versionData.frontend.version}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }
                          >
                            Build:
                          </span>
                          <span
                            className={`font-mono font-semibold ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            #{versionData.frontend.build_number}
                          </span>
                        </div>
                        {versionData.frontend.git_commit && (
                          <div className="flex justify-between items-center">
                            <span
                              className={
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }
                            >
                              Commit:
                            </span>
                            <code
                              className={`px-2 py-0.5 rounded text-xs ${
                                isDarkMode
                                  ? "bg-gray-600 text-white"
                                  : "bg-gray-200 text-gray-900"
                              }`}
                            >
                              {versionData.frontend.git_commit}
                            </code>
                          </div>
                        )}
                        <div className="flex justify-between items-center">
                          <span
                            className={`flex items-center gap-1.5 ${
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            <Calendar className="w-4 h-4" />
                            Deployed:
                          </span>
                          <span
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {formatDate(versionData.frontend.deployed_at)}
                          </span>
                        </div>
                        {versionData.frontend.deployed_by && (
                          <div className="flex justify-between items-center">
                            <span
                              className={`flex items-center gap-1.5 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              <User className="w-4 h-4" />
                              By:
                            </span>
                            <span
                              className={
                                isDarkMode ? "text-white" : "text-gray-900"
                              }
                            >
                              {versionData.frontend.deployed_by}
                            </span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}

                  {/* Backend Info */}
                  {versionData?.backend && (
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className={`border-l-4 border-green-500 p-4 rounded-r-lg ${
                        isDarkMode ? "bg-gray-700/50" : "bg-green-50"
                      }`}
                    >
                      <h3
                        className={`text-lg font-bold mb-3 flex items-center gap-2 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        <Activity className="w-5 h-5 text-green-500" />
                        Backend
                      </h3>
                      <div className="space-y-2.5 text-sm">
                        <div className="flex justify-between items-center">
                          <span
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }
                          >
                            Version:
                          </span>
                          <span
                            className={`font-mono font-bold text-base ${
                              isDarkMode ? "text-green-400" : "text-green-600"
                            }`}
                          >
                            v{versionData.backend.version}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }
                          >
                            Build:
                          </span>
                          <span
                            className={`font-mono font-semibold ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            #{versionData.backend.build_number}
                          </span>
                        </div>
                        {versionData.backend.git_commit && (
                          <div className="flex justify-between items-center">
                            <span
                              className={
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }
                            >
                              Commit:
                            </span>
                            <code
                              className={`px-2 py-0.5 rounded text-xs ${
                                isDarkMode
                                  ? "bg-gray-600 text-white"
                                  : "bg-gray-200 text-gray-900"
                              }`}
                            >
                              {versionData.backend.git_commit}
                            </code>
                          </div>
                        )}
                        <div className="flex justify-between items-center">
                          <span
                            className={`flex items-center gap-1.5 ${
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            <Calendar className="w-4 h-4" />
                            Deployed:
                          </span>
                          <span
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {formatDate(versionData.backend.deployed_at)}
                          </span>
                        </div>
                        {versionData.backend.deployed_by && (
                          <div className="flex justify-between items-center">
                            <span
                              className={`flex items-center gap-1.5 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              <User className="w-4 h-4" />
                              By:
                            </span>
                            <span
                              className={
                                isDarkMode ? "text-white" : "text-gray-900"
                              }
                            >
                              {versionData.backend.deployed_by}
                            </span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}

                  {/* System Status */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className={`pt-4 border-t ${
                      isDarkMode ? "border-gray-700" : "border-gray-200"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            isOnline ? "bg-green-500" : "bg-red-500"
                          }`}
                        />
                        {isOnline && (
                          <div className="absolute inset-0 w-3 h-3 rounded-full bg-green-500 animate-ping opacity-75" />
                        )}
                      </div>
                      <span
                        className={`text-sm font-semibold ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Status:{" "}
                        <span
                          className={
                            isOnline ? "text-green-500" : "text-red-500"
                          }
                        >
                          {isOnline ? "Operating System" : "System Offline"}
                        </span>
                      </span>
                    </div>
                  </motion.div>
                </div>

                {/* Footer */}
                <div
                  className={`px-6 py-4 border-t ${
                    isDarkMode ? "border-gray-700" : "border-gray-200"
                  }`}
                >
                  <div className="flex justify-end">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowModal(false)}
                      className="px-5 py-2.5 bg-gradient-to-r from-phoenix-600 to-phoenix-700 hover:from-phoenix-700 hover:to-phoenix-800 text-white rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl"
                    >
                      Close
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default VersionBadge;
